// Sign-in. Production: email + password (Supabase Auth).
// Local dev: pick one of the demo users loaded by supabase/seed.sql.
// Footer: Powered by + app version (comment #2, 03/Oct/26).
// A deactivated account (Part 3) is signed out again and the reason shows here (notice).
// Round 23 (board A0): the live sign-in card — Email (remembered), Password with show / hide, Remember me
// (core/rememberMe.ts: ticked = stay signed in on this PC, unticked = signed out when the browser closes). No (i) here.
// v0.8.0 (round 28): "Forgot password?" beside Remember me → A0.1 (core/pages/AuthPages.tsx).
import { useEffect, useState } from "react";
import { EMAIL_KEY, readRemember } from "../rememberMe";
import { getBackend, type Backend, type DemoUser } from "../api/backend";
import { ErrorBox } from "../ui/kit";
import { VersionLine } from "../layout/Shell";
import { Icon } from "../ui/icons";
import { BrandMark, BrandWordmark } from "../ui/brand";

export function LoginPage({ onSignedIn, notice }: { onSignedIn: () => void; notice?: string | null }) {
  const [backend, setBackend] = useState<Backend | null>(null);
  const [demo, setDemo] = useState<DemoUser[]>([]);
  const [email, setEmail] = useState(() => { try { return localStorage.getItem(EMAIL_KEY) ?? ""; } catch { return ""; } });
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(() => { try { return readRemember(localStorage); } catch { return true; } });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { getBackend().then((b) => { setBackend(b); b.listDemoUsers?.().then(setDemo).catch((e) => setError(e.message)); }); }, []);
  const submit = async () => {
    setBusy(true); setError(null);
    try { await backend!.signInWithPassword(email, password, remember); onSignedIn(); } catch (e) { setError((e as Error).message); setBusy(false); }
  };
  return (
    <div className="login">
      <div className="login-card">
        <div className="brand big"><BrandMark size={40} /><BrandWordmark /></div>
        {(error || notice) && <ErrorBox message={(error ?? notice)!} />}
        {backend?.mode === "supabase" && (
          <form onSubmit={(e) => { e.preventDefault(); submit(); }} data-testid="signin-form">
            <h2 className="login-title">Sign in</h2>
            <label className="field"><span>Email</span><input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <label className="field"><span>Password</span>
              <span className="pw-wrap"><input type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" className="pw-eye" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw((v) => !v)}><Icon name={showPw ? "visibility_off" : "visibility"} /></button></span></label>
            <div className="row between remember-row">
              <label className="remember"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />Remember me</label>
              {/* v0.8.0 (round 28, A0 → A0.1): self-service reset */}
              <a className="forgot-link" href="#/forgot-password">Forgot password?</a></div>
            <button className="btn primary wide" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
          </form>
        )}
        {backend?.mode === "dev" && (
          <>
            <p className="muted">Local demo — sign in as:</p>
            <div className="demo-users">
              {demo.map((u) => (
                <button key={u.id} className="demo-user" onClick={async () => { await backend.signInAsDemo!(u.id); onSignedIn(); }}>
                  <b>{u.full_name}</b><small>{u.summary}</small>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <VersionLine />
    </div>
  );
}
